/**
 * Footnotes Section Component
 *
 * Displays footnotes for the selected deckline or all page footnotes.
 * Provides "View All" toggle to show all footnotes on the page.
 * Read-only display component.
 *
 * @returns Footnotes section component
 */

import './Footnotes.scss';

import { useDecklinesContext } from '@context/DecklinesContext';
import { useFootnotesContext } from '@context/FootnotesContext.tsx';
import { Paper } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Footnote } from '@services/api/deckManagement/graphql/schema/API';
import { isNil } from 'lodash';
import { CSSProperties, Ref, useCallback, useEffect, useMemo, useState } from 'react';
import { FootnotesSectionProps } from 'types/Footnotes.types';

const FootnotesSection = ({ ref, ...props }: FootnotesSectionProps & { ref?: Ref<HTMLDivElement> }) => {
  const { containerWidth } = props;
  const { selectedDeckline } = useDecklinesContext();
  const { footnotes, selectedHeaderFootnotes, setSelectedHeaderFootnotes } = useFootnotesContext();
  const [viewAllToggle, setViewAllToggle] = useState<boolean>(true);

  /**
   * When a deckline is selected, disable `View All` so only that deckline's footnotes show, and
   * clear any header-footnote selection so the two sources stay mutually exclusive (a header click
   * conversely clears the deckline selection — see SectionGrid.handleFootnoteClick).
   */
  useEffect(() => {
    if (!isNil(selectedDeckline)) {
      setViewAllToggle(false);
      setSelectedHeaderFootnotes(null);
    }
  }, [selectedDeckline, setSelectedHeaderFootnotes]);

  /**
   * When a column header is selected, disable `View All` so only that header's footnotes show.
   */
  useEffect(() => {
    if (!isNil(selectedHeaderFootnotes)) {
      setViewAllToggle(false);
    }
  }, [selectedHeaderFootnotes]);

  // Handles populating CSS variables which are optional.
  const cssStyle = useMemo(() => {
    const cssStyleBuffer: Record<string, string | number> = {};
    if (containerWidth) {
      cssStyleBuffer['--footnotes-container-width'] = `${containerWidth}px`;
    }

    return cssStyleBuffer as CSSProperties;
  }, [containerWidth]);

  /**
   * Generate footnotes content for display
   * Filters footnotes based on selected deckline or view all toggle
   *
   * @returns TextField with formatted footnotes or null if no footnotes to display
   */
  const generateFootnotesContent = useCallback(() => {
    // The active non-"View All" selection is either a clicked column header or a selected deckline
    // (they are mutually exclusive). A header selection takes precedence when present.
    const activeSelection = !isNil(selectedHeaderFootnotes)
      ? { label: selectedHeaderFootnotes.label, footnotes: selectedHeaderFootnotes.footnotes }
      : selectedDeckline
        ? { label: selectedDeckline.metadata.actual_kpi_name || '', footnotes: selectedDeckline.footnotes || [] }
        : null;

    const label = viewAllToggle ? 'All' : activeSelection?.label || '';
    const filteredFootnotes = viewAllToggle
      ? footnotes
      : footnotes.filter((footnote: Footnote) =>
          (activeSelection?.footnotes || []).includes(parseInt(footnote.footnote_no!, 10)),
        );
    let footnotesContent = '';
    filteredFootnotes.forEach((footnote: Footnote) => {
      footnotesContent += `${footnote.footnote_no} ${footnote.footnote_description}\n`;
    });
    footnotesContent = footnotesContent.substring(0, footnotesContent.length - 1);
    // return null if no footnotes to display
    if ((!viewAllToggle && !activeSelection) || footnotesContent.trim().length === 0) {
      return null;
    }

    return (
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        value={footnotesContent}
        disabled
        label={label}
        margin='normal'
        className='footnotes-section-content'
        autoComplete='off'
      />
    );
  }, [viewAllToggle, footnotes, selectedDeckline, selectedHeaderFootnotes]);

  return (
    <Paper className='footnotes-section' style={cssStyle} elevation={6} ref={ref}>
      <Box className='footnotes-section-header'>
        <Typography variant='h6'>
          {viewAllToggle && 'All'} Footnotes {footnotes.length === 0 && '(None)'}
        </Typography>
        {footnotes.length > 0 && (
          <Button
            size='small'
            onClick={() => {
              // Toggling back to "View All" clears any header selection so the pane isn't stuck
              // filtered to a header once expanded. Both setters run directly in the handler — the
              // clear must NOT live inside the setViewAllToggle updater, which React runs during
              // render (updating FootnoteProvider from there triggers a setState-in-render warning).
              if (!viewAllToggle) {
                setSelectedHeaderFootnotes(null);
              }
              setViewAllToggle((prev) => !prev);
            }}
          >
            {viewAllToggle ? 'Collapse' : 'View All'}
          </Button>
        )}
      </Box>
      {generateFootnotesContent()}
    </Paper>
  );
};

export default FootnotesSection;
